// src/lib/navigation.js
//
// Los destinos de la app, en un solo sitio. Antes esta tabla vivía partida: el
// NAV del Sidebar tenía los nombres y los iconos, y el switch de AppShell tenía
// las rutas — así que añadir un destino eran dos ediciones que se podían
// desincronizar. Con el command palette hubiera sido una tercera copia.
//
// `id` es el identificador interno: lo consume AppShell y es lo que compara el
// resaltado del Sidebar. No se traduce y no debería cambiar.
// `label` es el respaldo en inglés; lo que se ve sale de i18n vía `i18nKey`.
// `aliases` son los términos por los que alguien puede buscar un destino sin
// saber cómo se llama hoy — incluidos los nombres viejos, para que renombrar
// (Épica 2) no rompa a quien ya se había aprendido la palabra anterior.
import {
  Home,
  Activity,
  Plug,
  FolderOpen,
  Database,
  Library,
  ShoppingBag,
  Workflow,
  Headset,
  LifeBuoy,
} from "lucide-react";
import ZenoIcon from "../components/Sidebar/ZenoIcon";

export const DESTINATIONS = [
  {
    id: "Home",
    label: "Home",
    i18nKey: "sidebar.home",
    Icon: Home,
    path: "/home",
    aliases: ["inicio", "dashboard", "overview", "portada", "casa"],
  },
  {
    id: "Zeno",
    label: "Zeno",
    i18nKey: "sidebar.zeno",
    Icon: ZenoIcon,
    path: "/",
    aliases: ["chat", "copilot", "asistente", "ai", "conversacion"],
  },
  {
    id: "Projects",
    label: "Projects",
    i18nKey: "sidebar.projects",
    Icon: FolderOpen,
    view: "Projects",
    aliases: ["proyectos", "workspaces", "espacios"],
    // Fuera del rail, pero sigue siendo un lugar: se llega por ⌘K, desde Home y
    // desde los enlaces que ya existen, y `?view=Projects` no cambia. Quitarlo
    // de la tabla habría sido quitarlo también del buscador, que es donde va a
    // buscarlo quien se sepa la palabra.
    hidden: true,
  },
  {
    id: "Assistants",
    label: "Agents",
    i18nKey: "sidebar.agents",
    Icon: Headset,
    view: "Agents",
    // "assistants" es el término del backend y el id interno: alguien del equipo
    // lo va a escribir aunque el UI diga "Agents".
    // Absorbe los sinónimos de Avatars: ahora viven en su pestaña, y quien
    // escriba "plantillas" tiene que seguir llegando.
    aliases: ["agentes", "assistants", "asistentes", "bots", "avatares", "avatars", "templates", "plantillas", "assistant templates"],
  },
  {
    id: "Workflows",
    label: "Workflows",
    i18nKey: "sidebar.workflows",
    Icon: Workflow,
    view: "Workflows",
    aliases: ["flujos", "automatizaciones", "automations", "flows"],
  },
  {
    id: "Shop",
    label: "Shop",
    i18nKey: "sidebar.shop",
    Icon: ShoppingBag,
    view: "Shop",
    aliases: ["tienda", "marketplace", "comprar", "coins", "monedas", "bundles"],
  },
  {
    id: "Knowledge",
    label: "Knowledge",
    i18nKey: "sidebar.knowledge",
    Icon: Library,
    view: "Knowledge",
    aliases: ["conocimiento", "kb", "knowledge base", "bases de conocimiento", "documentos", "rag"],
  },
  {
    id: "Database",
    label: "Data",
    i18nKey: "sidebar.data",
    Icon: Database,
    view: "Database",
    aliases: ["base de datos", "datos", "database", "tablas", "tables", "sql"],
  },
  {
    id: "Connect",
    label: "Connections",
    i18nKey: "sidebar.connections",
    Icon: Plug,
    view: "Integrations",
    // Absorbe los de Credentials por el mismo motivo que Agents los de Avatars.
    aliases: ["conectar", "conexiones", "connect", "integraciones", "integrations", "gmail", "whatsapp", "shopify", "credenciales", "credentials", "api keys", "llaves", "keys", "secretos", "tokens"],
  },
  {
    id: "Dashboard",
    // Se llama Activity y no Analytics desde que la seccion tiene las corridas:
    // el nombre ya describe lo que hay dentro. Antes habria sido una promesa
    // que la pagina no cumplia.
    label: "Activity",
    i18nKey: "sidebar.activity",
    Icon: Activity,
    path: "/dashboard",
    aliases: ["analitica", "analytics", "metricas", "metrics", "actividad", "activity", "uso", "usage"],
  },
];

// Vistas que dejaron de ser destino propio y ahora son una pestaña dentro de
// otro. Se mantienen para que los enlaces viejos (y quien tenga la URL guardada)
// sigan llevando al sitio correcto en vez de a una pantalla en blanco.
export const LEGACY_VIEWS = {
  Avatars: { view: "Agents", tab: "avatars" },
  Credentials: { view: "Integrations", tab: "credentials" },
};

/**
 * A dónde reescribir una URL que todavía usa una vista vieja, o `null` si no lo
 * es.
 *
 * Reescribe `view` y `tab`, y **conserva todo lo demás**. Eso último es la
 * razón de que esta función exista: la reescritura se hacía armando la URL
 * desde cero, así que cualquier otro parámetro se perdía en el camino. Como
 * abrir un avatar es poner `?templateId=` en la URL, y el enlace de una tarjeta
 * seguía diciendo `view=Avatars`, el id se borraba en el mismo instante en que
 * se escribía: se hacía clic en un avatar y reaparecía la lista.
 *
 * @param {string|URLSearchParams} search  la query actual
 * @returns {string|null}
 */
/**
 * A dónde entrar para crear un agente o un workflow dentro de un proyecto.
 *
 * Los dos exigen `project_id` en el backend, así que "New agent" no puede ser
 * un enlace fijo: primero hay que saber el proyecto. `new=` lo lee
 * `ProjectDetail`, que es quien sabe abrir el asistente de creación y el canvas
 * en blanco; aquí solo se dice cuál de los dos.
 *
 * @param {"new-agent"|"new-workflow"} intent
 * @param {string} projectId
 */
export function creationPath(intent, projectId) {
  const what = intent === "new-workflow" ? "workflow" : "agent";
  return `/dashboard?view=Projects&projectId=${encodeURIComponent(
    projectId
  )}&tab=assistants&new=${what}`;
}

export function legacyViewRedirect(search) {
  const params = new URLSearchParams(search);
  const legacy = LEGACY_VIEWS[params.get("view")];
  if (!legacy) return null;
  params.set("view", legacy.view);
  params.set("tab", legacy.tab);
  return `/dashboard?${params.toString()}`;
}

// Dónde van los separadores del rail: después de estos ids. Viven aquí y no en
// el Sidebar para que el orden y el agrupamiento se lean juntos.
export const SEPARATOR_AFTER = new Set(["Zeno", "Workflows", "Shop", "Connect"]);

// Lo que se pinta arriba, en la zona de objetos.
export const RAIL_DESTINATIONS = DESTINATIONS.filter((d) => !d.hidden);

// Utilidades: no son objetos de la app, son ayuda. La convención dice que van en
// la barra superior junto a la búsqueda y el menú de cuenta — pero aquí el rail
// es el único cromo persistente, así que viven abajo, separadas de los
// sustantivos y pegadas al avatar, que es la otra utilidad.
export const RAIL_UTILITIES = [
  {
    id: "Support",
    label: "Support",
    i18nKey: "sidebar.support",
    Icon: LifeBuoy,
    path: "/support",
    aliases: ["soporte", "ayuda", "help", "contacto", "contact", "problema", "bug"],
  },
];

const BY_ID = new Map(
  [...DESTINATIONS, ...RAIL_UTILITIES].map((d) => [d.id, d])
);

export const destinationById = (id) => BY_ID.get(id);

/** Ruta a la que navega un destino. Los que no tienen `path` cuelgan de /dashboard. */
export function pathForDestination(id) {
  const d = BY_ID.get(id);
  if (!d) return null;
  return d.path || `/dashboard?view=${d.view}`;
}

/** Inverso: qué destino corresponde a la ubicación actual, para el resaltado. */
export function destinationIdForLocation(pathname, search = "") {
  if (pathname === "/") return "Zeno";
  if (pathname === "/home") return "Home";
  if (pathname === "/support") return "Support";
  if (pathname === "/dashboard") {
    const view = new URLSearchParams(search).get("view");
    if (!view) return "Dashboard";
    const legacy = LEGACY_VIEWS[view];
    const target = legacy ? legacy.view : view;
    return DESTINATIONS.find((d) => d.view === target)?.id || target;
  }
  return "";
}
